import { useEffect, useRef } from 'react';
import { AiLabel, Drawer, ProcessingIndicator } from '@kainos/ui';
import { useUi } from '@/store/ui-store';
import { getSuggestedQuestions } from '../api/ask-assistant';
import { useAiChat } from '../hooks/useAiChat';
import { ChatComposer } from './ChatComposer';
import { ChatMessage } from './ChatMessage';
import { ChatSuggestions } from './ChatSuggestions';

/**
 * The KainOS AI panel.
 *
 * Mounted once by the shell, opened from the sidebar, the dashboard or any
 * attention card through the UI store.
 */
export function AiChatDrawer() {
  const { aiDrawerOpen, aiDrawerSeed, closeAiDrawer } = useUi();
  const { messages, isPending, ask } = useAiChat(aiDrawerOpen ? aiDrawerSeed : null);
  const endRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, isPending]);

  return (
    <Drawer
      open={aiDrawerOpen}
      onClose={closeAiDrawer}
      title={<AiLabel variant="insight" tag="Asistente" />}
      footer={
        <>
          <ChatSuggestions
            questions={getSuggestedQuestions()}
            onSelect={(question) => void ask(question)}
            disabled={isPending}
          />
          <ChatComposer onSubmit={(question) => void ask(question)} disabled={isPending} />
        </>
      }
    >
      {messages.map((message) => (
        <ChatMessage key={message.id} message={message} onNavigate={closeAiDrawer} />
      ))}

      {isPending ? <ProcessingIndicator label="KainOS AI está revisando tus proyectos…" /> : null}

      <div ref={endRef} />
    </Drawer>
  );
}
